import {makeAutoObservable, observable, runInAction} from "mobx";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IRosterProvider} from "data/providers/api/roster.provider";
import {Empty} from "data/types/global";
import {times, last, identity, sortBy} from "lodash";
import {RosterSelectionType} from "data/enums";
import type {IRosterItem, IRosterPlayer, IRosterSpin} from "data/types/roster";

export interface IRosterStore {
	get isLockScreen(): boolean;

	get isWheelView(): boolean;

	get playedContests(): number[] | null;

	get nextContestId(): Empty<number>;

	get prevContestId(): Empty<number>;

	get currentContestId(): number;

	get isRosterFull(): boolean;

	get isLastRosterCell(): boolean;

	get rosterSize(): number;

	get isLimitReached(): boolean;

	get spinsCount(): number;

	get rosterPlayersCount(): number;

	get currentSpinnerIds(): number[];

	get restSpinPlayerIds(): number[];

	get currentRoster(): Empty<IRosterItem>;

	get rosterPlayers(): IRosterPlayer[];

	get beforeSpinRosterPlayers(): IRosterPlayer[];

	get beforeSpinPlayerIds(): number[];

	get potentialPrize(): Empty<IRosterSpin>;

	rosterPlayersByContestId(contestId: number): IRosterPlayer[];
	fetchRoster(): Promise<void>;
	fetchPlayedContests(): Promise<void>;
	saveSpin(): Promise<Empty<IRosterSpin>>;
	addPlayer(): Promise<void>;

	updatePlayers: () => void;
	updateLineupPlayers: () => void;
	updateContestId: (contestId: number) => void;
	updateView: (state: boolean) => void;
	removeLockScreen: () => void;
}

@injectable()
export class RosterStore implements IRosterStore {
	@observable private _contestId = 0;
	@observable private _isWheelView = true;
	@observable private _lockScreen = false;
	@observable private _rosters: Record<number, IRosterItem> = {};
	@observable private _playedContests: number[] | null = null;
	@observable private _rostersBeforeSave: Record<number, IRosterItem> = {};
	@observable private _rosterRequest: ReturnType<IRosterProvider["getRoster"]> | undefined;

	constructor(@inject(Bindings.RosterProvider) private _rosterApiProvider: IRosterProvider) {
		makeAutoObservable(this);
	}

	get isLockScreen(): boolean {
		return this._lockScreen;
	}

	get isWheelView(): boolean {
		return this._isWheelView;
	}

	get playedContests(): number[] | null {
		return sortBy(this._playedContests, identity);
	}

	get nextContestId(): Empty<number> {
		const index = this.playedContests?.findIndex((it) => it === this._contestId) ?? -1;
		if (index === -1) {
			return;
		}
		return this.playedContests?.[index + 1];
	}

	get prevContestId(): Empty<number> {
		const contestsIDs = this.playedContests || [];
		const index = contestsIDs.findIndex((it) => it === this._contestId) ?? -1;
		if (index === -1 && contestsIDs.length) {
			return contestsIDs[contestsIDs.length - 1];
		}
		return contestsIDs[index - 1];
	}

	get currentContestId() {
		return this._contestId;
	}

	updateContestId = (contestId: number) => {
		this._contestId = contestId;
	};

	get rosterSize(): number {
		return 5;
	}

	get isLimitReached(): boolean {
		return !!this.beforeSpinRosterPlayers.find(
			(it) => it.selectionType === RosterSelectionType.AUTO
		);
	}

	get isRosterFull(): boolean {
		return this._rosters[this._contestId]?.players.length === this.rosterSize;
	}

	get isLastRosterCell(): boolean {
		return this._rosters[this._contestId]?.players.length === this.rosterSize - 1;
	}

	get spinsCount(): number {
		return this._rostersBeforeSave[this._contestId]?.spins.length || 0;
	}

	get rosterPlayersCount(): number {
		return this._rosters[this._contestId]?.players?.length || 0;
	}

	get currentSpinnerIds(): number[] {
		return this.currentRoster?.spinPlayerIds || [];
	}

	get currentRoster() {
		return this._rosters[this._contestId];
	}

	get beforeSpinRosterPlayers() {
		return this._rostersBeforeSave[this._contestId]?.players || [];
	}

	get beforeSpinPlayerIds() {
		return this._rostersBeforeSave[this._contestId]?.spinPlayerIds || [];
	}

	get potentialPrize() {
		return last(this._rostersBeforeSave[this._contestId]?.spins);
	}

	get restSpinPlayerIds() {
		const roster = this.currentRoster;
		const potentialPrize = this.potentialPrize;

		return roster?.spinPlayerIds.filter((playerId) => {
			if (potentialPrize?.playerId === playerId) {
				return false;
			}
			return !roster.players.find((player) => player.playerId === playerId);
		});
	}

	get rosterPlayers() {
		const roster = this.currentRoster;
		return times(this.rosterSize, (index) => {
			if (roster && roster.players[index]) {
				return roster.players[index];
			}

			return {
				position: index,
				playerId: 0,
				selectionType: RosterSelectionType.BY_USER,
				spinNumber: 0,
				points: 0,
			};
		});
	}

	rosterPlayersByContestId(contestId: number): IRosterPlayer[] {
		return this._rosters[contestId]?.players || [];
	}

	async fetchRoster() {
		if (this._rosterRequest) {
			await this._rosterRequest;
			return;
		}

		this._rosterRequest = this._rosterApiProvider.getRoster(this._contestId);
		const response = await this._rosterRequest;
		const responseRoster = response.data.success.roster;

		runInAction(() => {
			this._rosters[this._contestId] = responseRoster;
			this._rostersBeforeSave[this._contestId] = responseRoster;
			this._isWheelView = !this.isRosterFull;
			this._rosterRequest = undefined;
		});
	}

	async fetchPlayedContests() {
		const response = await this._rosterApiProvider.playedContests();
		const responseContestIds = response.data.success.contestIds;

		runInAction(() => {
			this._playedContests = responseContestIds;
		});
	}

	async saveSpin(): Promise<Empty<IRosterSpin>> {
		const response = await this._rosterApiProvider.saveSpin(this._contestId);
		const responseRoster = response.data.success.roster;

		runInAction(() => {
			this._lockScreen = true;
			this._rostersBeforeSave[this._contestId] = responseRoster;
		});

		return last(responseRoster.spins);
	}

	async addPlayer() {
		const response = await this._rosterApiProvider.addPlayer(this._contestId);
		runInAction(() => {
			this._lockScreen = false;
			this._rosters = {
				...this._rosters,
				[this._contestId]: response.data.success.roster,
			};
		});
	}

	updatePlayers = () => {
		this._rosters = {
			...this._rosters,
			...this._rostersBeforeSave,
		};
	};

	updateLineupPlayers = () => {
		if (this._rostersBeforeSave[this._contestId]?.players.length) {
			this._rosters[this._contestId] = {
				...this._rosters[this._contestId],
				players: this._rostersBeforeSave[this._contestId]?.players,
			};
		}
	};

	updateView = (state: boolean) => {
		this._isWheelView = state;
	};

	removeLockScreen = () => {
		this._lockScreen = false;
	};
}
